import React from 'react';
import bemBlock from '@gik/core/utils/bemBlock';
import { CalendarEvent } from './CalendarEvent';
import { Moment } from 'moment';
import {
  ICalendarAnnouncement,
  ICalendarEntry,
  ICalendarEntryBase,
  ICalendarEvent,
} from '@gik/calendar/models/Calendar';
import { CalendarEventTypeIds } from '@gik/calendar/models/CalendarEventTypes';
import CalendarAnnouncement from '../Announcements/CalendarAnnouncement';
import { HeroIconName, LoadingSpinner } from '@gik/ui';
import { useCalendarAnnouncementTypes } from '@gik/api/calendar/calendarAnnouncementType';
import { useCalendarEventTypes } from '@gik/api/calendar/calendarEventType';
import { useUserStore } from '@gik/core/store/UserStore';
import { CalendarUtil } from '@gik/calendar';
import { canUserEditPage } from '@gik/inkind-page/utils/inkindPagePermissions';
import { useInkind } from '@gik/api/inkinds/inkind';
import { useInkindStore } from '@gik/inkind/store/InkindStore';
import { translationKeys } from './i18n/en';
import { useTranslation } from 'react-i18next';

export interface ICalendarDayProps extends React.HTMLAttributes<HTMLDivElement> {
  date?: Moment;
  entries?: ICalendarEntryBase[];
  events?: ICalendarEvent[];
  resolvingClaim?: boolean;
  onAddEvent?(date: Moment): void;
  onEditEvent?(event: ICalendarEvent, entry: ICalendarAnnouncement | ICalendarEntry): void;
  onClaimEvent?(event: ICalendarEvent, entry: ICalendarAnnouncement | ICalendarEntry): void;
}

export default function CalendarDay({
  date,
  className,
  entries,
  events,
  resolvingClaim,
  onAddEvent,
  onEditEvent,
  onClaimEvent,
  ...otherProps
}: ICalendarDayProps): React.ReactElement {
  const bem = bemBlock('calendar-day');
  const { t } = useTranslation();

  const { data: eventTypes } = useCalendarEventTypes();
  const { data: announcementTypes } = useCalendarAnnouncementTypes();

  const userId = useUserStore(state => state.id);
  const inkindRouteId = useInkindStore(state => state.inkindRouteId);
  const { data: inkind } = useInkind(inkindRouteId);

  const canEdit = canUserEditPage(inkind, userId);

  function handleDayClick() {
    onAddEvent(date);
  }

  function handleAnnouncementClick(
    ev: React.MouseEvent<HTMLDivElement>,
    event: ICalendarEvent,
    entry: ICalendarEntryBase
  ) {
    ev.stopPropagation();
    if (canEdit) {
      CalendarUtil.editAnnouncement(event, entry);
    } else {
      CalendarUtil.viewAnnouncement(event, entry);
    }
  }

  function customMoreButtonClick(
    ev: React.MouseEvent<HTMLDivElement>,
    event: ICalendarEvent,
    entry: ICalendarAnnouncement
  ) {
    ev.stopPropagation();
    CalendarUtil.viewAnnouncement(event, entry);
  }

  const isEmpty = !events || !events.length;

  const isLoading = !announcementTypes || !eventTypes;
  if (isLoading) {
    return <LoadingSpinner center />;
  }

  return (
    <div
      className={bem(null, [{ empty: isEmpty }, { clickable: canEdit }], className)}
      {...otherProps}
      onClick={handleDayClick}
    >
      <header className={bem('header')}>
        <span className={bem('date')}>
          <span>
            <div className={bem('date-day-name')}>{date.format('ddd')}</div>
            <div className={bem('date-month')}>{date.format('MMM')}</div>
          </span>
          <div className={bem('date-day')}>{date.format('D')}</div>
        </span>
      </header>
      {!isEmpty ? (
        <div className={bem('list')}>
          {events.map(event => {
            //get entry
            const filtered = entries.filter(item => item.id === event.entryId);

            if (filtered.length > 1) {
              return <div key={event.entryId}>Error: more than 1 item found</div>;
            }

            const entry = filtered[0];

            if (!entry) {
              return <div key={event.entryId}>{`entry ${event.entryId} not found`}</div>;
            }

            if (entry.typeId === CalendarEventTypeIds.Announcement) {
              const announcementEntry = entry as ICalendarAnnouncement;

              const announcementType = announcementTypes.find(item => item.id === announcementEntry.announcementTypeId);

              if (!announcementType) {
                console.warn(
                  `announcementType "${announcementEntry.announcementTypeId}" not found for entry "${announcementEntry.id}"`
                );
              }

              return (
                <CalendarAnnouncement
                  key={announcementEntry.id}
                  data-id={announcementEntry.id}
                  type={announcementType?.slug || 'info'}
                  icon={(announcementType?.acf.icon || 'Unknown') as HeroIconName}
                  startsAt={announcementEntry.startsAt}
                  endsAt={announcementEntry.endsAt}
                  allDay={announcementEntry.allDay}
                  title={announcementEntry.title}
                  description={announcementEntry.description}
                  onClick={ev => handleAnnouncementClick(ev, event, announcementEntry)}
                  customMoreButtonClick={ev => customMoreButtonClick(ev, event, announcementEntry)}
                />
              );
            } else {
              const calendarEntry = entry as ICalendarEntry;
              return (
                <CalendarEvent
                  key={entry.id}
                  data-id={entry.id}
                  eventTypes={eventTypes}
                  event={event}
                  entry={calendarEntry}
                  onEdit={(event, entry) => onEditEvent(event, entry)}
                  onClaim={onClaimEvent}
                  resolvingClaim={resolvingClaim}
                />
              );
            }
          })}
        </div>
      ) : (
        <div className={bem('no-requests')}>
          <span>{t(translationKeys.calendarDayNoRequest)}</span>
        </div>
      )}
    </div>
  );
}
